/*
 * Lens phase — 5 steps shared with the Workbench. Reads/writes
 * motr-lens-v1 via @shared/lens. Renders the prefilled banner if the
 * lens arrived from the Workbench. Each step locks Continue until
 * isLensStepValid passes.
 */
import { useEffect, useMemo } from 'react';
import {
  LENS_EMOTIONAL_AVOID,
  LENS_EMOTIONAL_FEEL,
  LENS_FLEX_LEVELS,
  LENS_FRICTION_PHASES,
  LENS_OUTCOME_PLACEHOLDERS,
  findRedFlagWords,
  isLensStepValid,
  type LensFlexibility,
  type LensFrictionPhase,
  type SharedLens,
} from '@shared/lens';
import { usePlanBuilderConfigContext } from '../PlanBuilderConfigContext';

export type LensStep = 1 | 2 | 3 | 4 | 5;

interface Props {
  step: LensStep;
  lens: SharedLens;
  wasFromOtherTool: boolean;
  onLensChange(next: SharedLens): void;
  onBack(): void;
  onNext(): void;
}

export function LensPhase({
  step,
  lens,
  wasFromOtherTool,
  onLensChange,
  onBack,
  onNext,
}: Props) {
  const valid = isLensStepValid(lens, step);
  const progress = (step / 5) * 100;

  return (
    <div className="pb-lens-wrap">
      <div className="pb-lens-progress-wrap">
        <div className="pb-lens-progress-meta">
          <span className="pb-lens-progress-label">
            Lens · Build your evaluation frame
          </span>
          <span className="pb-lens-progress-count">Step {step} of 5</span>
        </div>
        <div className="pb-lens-progress-track">
          <div
            className="pb-lens-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {step === 1 && wasFromOtherTool && (
        <div className="pb-lens-prefilled-banner">
          <strong>We pulled in your lens from the Due Diligence Workbench.</strong>{' '}
          Edit any answer below — changes save automatically and update both
          tools.
        </div>
      )}

      <div className="pb-lens-card">
        {step === 1 && <LensStep1 lens={lens} onLensChange={onLensChange} />}
        {step === 2 && <LensStep2 lens={lens} onLensChange={onLensChange} />}
        {step === 3 && <LensStep3 lens={lens} onLensChange={onLensChange} />}
        {step === 4 && <LensStep4 lens={lens} onLensChange={onLensChange} />}
        {step === 5 && <LensStep5 lens={lens} onLensChange={onLensChange} />}
      </div>

      <div className="pb-section-nav">
        <button type="button" className="pb-btn-back" onClick={onBack}>← Back</button>
        <span className="pb-section-counter" />
        <button
          type="button"
          className="pb-btn-next"
          onClick={onNext}
          disabled={!valid}
        >
          {step === 5 ? 'Continue to diagnostic →' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}

// ─── Step 1 — Job statement ────────────────────────────────────────
function LensStep1({
  lens,
  onLensChange,
}: {
  lens: SharedLens;
  onLensChange(l: SharedLens): void;
}) {
  const { lens: lensCopy } = usePlanBuilderConfigContext();

  // Recompute red-flag words on every change. We don't debounce here —
  // React's batching + the cheap regex scan keeps it fast, and the
  // banner reflects the latest state immediately rather than 400ms
  // later.
  useEffect(() => {
    const hits = findRedFlagWords(lens.jobStatement);
    if (
      hits.length !== lens.redFlagWordsHit.length ||
      hits.some((h, i) => h !== lens.redFlagWordsHit[i])
    ) {
      onLensChange({ ...lens, redFlagWordsHit: hits });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens.jobStatement]);

  return (
    <>
      <span className="pb-lens-tag">Step 1 of 5 · Job statement</span>
      <h2 className="pb-lens-title">
        {lensCopy.step1.heading}
      </h2>
      <p className="pb-lens-sub">
        {lensCopy.step1.body}
      </p>
      <div className="pb-lens-field">
        <label className="pb-lens-field-label" htmlFor="lens-job">
          Your job statement
        </label>
        <p className="pb-lens-field-hint">
          {lensCopy.step1.helper}
        </p>
        <textarea
          id="lens-job"
          className="pb-lens-textarea"
          rows={3}
          placeholder="Enable the business to execute agreements quickly while protecting the company from unacceptable risk."
          value={lens.jobStatement}
          onChange={(e) =>
            onLensChange({ ...lens, jobStatement: e.target.value })
          }
        />
        {lens.redFlagWordsHit.length > 0 && (
          <div className="pb-lens-coach" role="status">
            <svg
              className="pb-lens-coach-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
              />
            </svg>
            <div className="pb-lens-coach-body">
              <strong>Your statement names a solution.</strong> You used:{' '}
              {lens.redFlagWordsHit.slice(0, 3).map((h) => `"${h}"`).join(', ')}
              . Try restating in terms of the progress you're trying to make —
              that keeps the door open to options you haven't considered yet.
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Step 2 — Friction phase ───────────────────────────────────────
function LensStep2({
  lens,
  onLensChange,
}: {
  lens: SharedLens;
  onLensChange(l: SharedLens): void;
}) {
  const { lens: lensCopy } = usePlanBuilderConfigContext();

  return (
    <>
      <span className="pb-lens-tag">Step 2 of 5 · Friction</span>
      <h2 className="pb-lens-title">
        {lensCopy.step2.heading}
      </h2>
      <p className="pb-lens-sub">
        {lensCopy.step2.body}
      </p>
      <div className="pb-lens-field">
        <span className="pb-lens-field-label">
          The phase that creates the most friction
        </span>
        <p className="pb-lens-field-hint">
          {lensCopy.step2.helper}
        </p>
        <div className="pb-lens-pills" role="radiogroup" aria-label="Friction phase">
          {LENS_FRICTION_PHASES.map((p) => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={lens.frictionPhase === p.key}
              className={`pb-lens-pill${lens.frictionPhase === p.key ? ' selected' : ''}`}
              onClick={() =>
                onLensChange({ ...lens, frictionPhase: p.key as LensFrictionPhase })
              }
            >
              <span>{p.label}</span>
              <span className="pb-lens-pill-sub">{p.sub}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Step 3 — Three outcomes ───────────────────────────────────────
function LensStep3({
  lens,
  onLensChange,
}: {
  lens: SharedLens;
  onLensChange(l: SharedLens): void;
}) {
  const { lens: lensCopy } = usePlanBuilderConfigContext();

  const setOutcome = (i: 0 | 1 | 2, val: string) => {
    const next: [string, string, string] = [...lens.outcomes];
    next[i] = val;
    onLensChange({ ...lens, outcomes: next });
  };
  return (
    <>
      <span className="pb-lens-tag">Step 3 of 5 · Outcomes</span>
      <h2 className="pb-lens-title">
        {lensCopy.step3.heading}
      </h2>
      <p className="pb-lens-sub">
        {lensCopy.step3.body}
      </p>
      <div className="pb-lens-field">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--textMuted)',
                width: 80,
                flex: 'none',
              }}
            >
              Outcome {i + 1}
            </span>
            <input
              type="text"
              className="pb-lens-input"
              placeholder={LENS_OUTCOME_PLACEHOLDERS[i]}
              value={lens.outcomes[i] ?? ''}
              onChange={(e) => setOutcome(i as 0 | 1 | 2, e.target.value)}
            />
          </div>
        ))}
        <p className="pb-lens-field-hint" style={{ marginTop: 12 }}>
          {lensCopy.step3.helper}
        </p>
      </div>
    </>
  );
}

// ─── Step 4 — Emotional & social ───────────────────────────────────
function LensStep4({
  lens,
  onLensChange,
}: {
  lens: SharedLens;
  onLensChange(l: SharedLens): void;
}) {
  const { lens: lensCopy } = usePlanBuilderConfigContext();

  const toggle = (bucket: 'feel' | 'avoid', item: string, on: boolean) => {
    const arr = lens.emotional[bucket].slice();
    const idx = arr.indexOf(item);
    if (on && idx < 0) arr.push(item);
    if (!on && idx >= 0) arr.splice(idx, 1);
    onLensChange({
      ...lens,
      emotional: { ...lens.emotional, [bucket]: arr },
    });
  };
  return (
    <>
      <span className="pb-lens-tag">Step 4 of 5 · How it should feel</span>
      <h2 className="pb-lens-title">
        {lensCopy.step4.heading}
      </h2>
      <p className="pb-lens-sub">
        {lensCopy.step4.body}
      </p>
      <div className="pb-lens-field">
        <span className="pb-lens-field-label">
          Your team should feel and be perceived as…
        </span>
        <p className="pb-lens-field-hint">{lensCopy.step4.helper}</p>
        <div className="pb-lens-emo-grid">
          {LENS_EMOTIONAL_FEEL.map((item) => {
            const on = lens.emotional.feel.includes(item);
            return (
              <label
                key={item}
                className={`pb-lens-emo-chip${on ? ' selected feel' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => toggle('feel', item, e.target.checked)}
                />
                <span>{item}</span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="pb-lens-field">
        <span className="pb-lens-field-label">…and should stop</span>
        <p className="pb-lens-field-hint">
          The pain you want this work to make go away. Lost nights. Burnout.
          Reputational hits. Be honest — the things you don't say out loud are
          usually the ones that drive adoption.
        </p>
        <div className="pb-lens-emo-grid">
          {LENS_EMOTIONAL_AVOID.map((item) => {
            const on = lens.emotional.avoid.includes(item);
            return (
              <label
                key={item}
                className={`pb-lens-emo-chip${on ? ' selected avoid' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => toggle('avoid', item, e.target.checked)}
                />
                <span>{item}</span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Step 5 — Flexibility ───────────────────────────────────────────
function LensStep5({
  lens,
  onLensChange,
}: {
  lens: SharedLens;
  onLensChange(l: SharedLens): void;
}) {
  const { lens: lensCopy } = usePlanBuilderConfigContext();
  const flexLevels = useMemo(() => LENS_FLEX_LEVELS, []);

  return (
    <>
      <span className="pb-lens-tag">Step 5 of 5 · Flexibility</span>
      <h2 className="pb-lens-title">
        {lensCopy.step5.heading}
      </h2>
      <p className="pb-lens-sub">
        {lensCopy.step5.body}
      </p>
      <div className="pb-lens-field">
        <span className="pb-lens-field-label">Your flexibility requirement</span>
        <p className="pb-lens-field-hint">
          {lensCopy.step5.helper}
        </p>
        <div
          className="pb-lens-pills cols-3"
          role="radiogroup"
          aria-label="Flexibility level"
        >
          {flexLevels.map((f) => (
            <button
              key={f.key}
              type="button"
              role="radio"
              aria-checked={lens.flexibility === f.key}
              className={`pb-lens-pill${lens.flexibility === f.key ? ' selected' : ''}`}
              onClick={() =>
                onLensChange({ ...lens, flexibility: f.key as LensFlexibility })
              }
            >
              <span>{f.label}</span>
              <span className="pb-lens-pill-sub">{f.sub}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
